import React, { Fragment, useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalButton,
} from "baseui/modal";
import { FileUploader } from "baseui/file-uploader";
import { ProgressBarRounded } from "baseui/progress-bar";
import { Delete } from "baseui/icon";
import { Check } from "baseui/icon"; // Assuming a hypothetical Check icon
import { Block } from "baseui/block";
import { RadioGroup, Radio } from "baseui/radio";
import {
  createModelRunAsset,
  cancelUpload,
} from "../../redux/model-run/ModelRunThunk";
import { resetCompletedUploads } from "../../redux/model-run/ModelRunActions";
import useRouteParams from "../../hooks/useRouteParams";
function bytesToSize(bytes) {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "0 Byte";
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
  return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
}
const fileNameStyle = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "200px", // or whatever max width you prefer
  marginLeft: "15px",
};
const fileItemStyle = {
  display: "flex",
  alignItems: "center",
  marginBottom: "12px",
  width: "100%",
};

const UploadModalComponent = (props) => {
  const [fileType, setFileType] = React.useState("Pictures");
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const {pathParams, searchParams, searchPart} = useRouteParams();

  const [queue, setQueue] = useState([]); // Queue for files to be uploaded
  const [isUploading, setIsUploading] = useState(false);

  function getFileAcceptType() {
    switch (fileType) {
      case "Pictures":
        return ".jpg, .png";
      case "Documents":
        return ".pdf,.doc,.docx,.xls,.xlsx";
      case "Archives":
        return ".zip,.rar,.7z";
      default:
        return "*/*";
    }
  }
  const handleClose = () => {
    props.close();
  };

  const handleCancelUpload = () => {
    if(isUploading) {
        props.cancelUpload(); // Cancel ongoing uploads
        setQueue([]); // Clear the upload queue
        setIsUploading(false); // Reset uploading status
    } else {
        handleClose(); // Close the modal if no upload is in progress
    }
};
  const handleUpload = () => {
    if (!queue.length && selectedFiles.length) {
      setQueue([...selectedFiles]);
      const file = selectedFiles[0];
      startUpload(file);
    } else {
      const file = queue[0];
      startUpload(file);
    }
  };

  const startUpload = (file) => {
    setIsUploading(true);
    if (file && !(props.completedUploads || []).includes(file.name)) {
      props.oncreateModelRunAsset(pathParams['modelerId'], searchParams["runId"], fileType, file);
    } else if (file) {
      console.log(`File '${file.name}' already uploaded.`);
      setIsUploading(false);

    }
  };

  useEffect(() => {
    if (props.completedUploads.includes(queue[0]?.name)) {
      setQueue((prevQueue) => {
        const newQueue = prevQueue.slice(1);
        // If there are more files in the queue, start uploading the next one
        if (newQueue.length) {
          startUpload(newQueue[0]);
        }
        return newQueue;
      });
    }
  }, [props.completedUploads]);

  useEffect(() => {
    if (!queue.length && isUploading) {
      setIsUploading(false);
    }
  }, [queue]);

  const handleFileDrop = (acceptedFiles, rejectedFiles) => {
    setSelectedFiles(acceptedFiles);
    props.resetCompletedUploads(); // Clear previously completed uploads
    setQueue([]); // Clear the upload queue
  };

  useEffect(() => {
    setSelectedFiles([]);
  }, [fileType, props.isOpen]);

  const handleFileDelete = (fileToDelete) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((file) => file !== fileToDelete)
    );
  };

  return (
    <React.Fragment>
      <Modal
        overrides={{
          Root: {
            style: ({ $theme }) => ({
              zIndex: $theme.zIndex.modal + 10, // Increase the z-index value
            }),
          },
        }}
        closeable={false}
        onClose={handleClose}
        isOpen={props.isOpen}
      >
        <ModalHeader>Upload Files</ModalHeader>
        <ModalBody>
          <FileUploader
            overrides={{
              Root: {
                style: {
                  marginBottom: "16px",
                },
              },
            }}
            accept={getFileAcceptType()}
            onDrop={handleFileDrop}
          />
          {selectedFiles.map((file) => (
            <Block key={file.name} $style={fileItemStyle}>
              {/* Upload Status Indicator (Checkmark, Spinner, Delete) */}
              {props.completedUploads.includes(file.name) ? (
                <Check size={32} color="green" />
              ) : props.uploadProgress[file.name] &&
                props.uploadProgress[file.name] < 100 ? (
                <ProgressBarRounded
                  size="small"
                  progress={props.uploadProgress[file.name] / 100}
                />
              ) : (
                <Delete
                  onClick={() => handleFileDelete(file)}
                  size={32}
                  title="Remove file"
                  cursor="pointer"
                />
              )}

              {/* File Name with Text Ellipsis */}
              <span style={fileNameStyle}>{file.name}-</span>
              <span>{bytesToSize(file.size)}</span>
            </Block>
          ))}
          <RadioGroup
            value={fileType}
            onChange={(event) => setFileType(event.target.value)}
            overrides={{
              RadioGroupRoot: {
                style:{
                  display: "flex",
                  flexDirection: "row",
                },
              },
            }}
          >
            <Radio
              value="Pictures"
              overrides={{
                Root: {
                  style: {
                    marginRight: "16px",
                  },
                },
              }}
            >
              Pictures
            </Radio>
            <Radio
              value="Documents"
              overrides={{
                Root: {
                  style:{
                    marginRight: "16px",
                  },
                },
              }}
            >
              Documents
            </Radio>
            <Radio value="Archives">Archives</Radio>
          </RadioGroup>
        </ModalBody>
        <ModalFooter>
          <ModalButton
            kind="tertiary"
            onClick={handleCancelUpload}
            disabled={props.cancelCount > 0}
          >
            Cancel
          </ModalButton>
          <ModalButton
            onClick={handleUpload}
            disabled={isUploading || selectedFiles.length === 0}
          >
            Upload
          </ModalButton>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    activeModeler: state.modeler.activeModeler,
    modelRuns: state.modelRun.modelRuns,
    selectedModelRun: state.modelRun.selectedModelRun,
    cancelCount: state.modelRun.cancelTokens?.length || 0,
    inProgressCount: state.modelRun.uploadProgress?.length || 0,
    errors: state.modelRun.errors,
    completedUploads: state.modelRun.completedUploads,
    uploadProgress: state.modelRun.uploadProgress,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    resetCompletedUploads: () => {
      dispatch(resetCompletedUploads());
    },
    oncreateModelRunAsset: (modelerId, modelRunId, fileType, file) => {
      dispatch(createModelRunAsset(modelerId, modelRunId, fileType, file));
    },
    cancelUpload: (filename) => {
      dispatch(cancelUpload(filename));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UploadModalComponent);
